import { useCallback } from 'react';
import {
  GenericError,
  InternalServerError,
  MissingParameterError,
  NotFoundError,
  UnauthorizedError,
} from 'src/api/common/error/HttpError';
import { useErisErrorStore } from '../../store/ErisErrorStore';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useErisInformationStore } from '../../store/ErisInformationStore';
import { ApiEnvelope } from 'src/api/common/api/ApiEnvelope';
import erisAxiosInstance from './erisAxiosInstance';
import { useTranslation } from 'react-i18next';

function useErisApi() {
  const { setIsSearchingResults } = useErisInformationStore();
  const { addError, clearErrors } = useErisErrorStore();
  const { t } = useTranslation();

  const get = useCallback(
    async <T>(url: string, config?: AxiosRequestConfig): Promise<T | null> => {
      setIsSearchingResults(true);
      clearErrors();
      try {
        const response = await erisAxiosInstance.get<ApiEnvelope<T>>(url, config);
        const { status, data, request } = response;

        if (status === 200 && data.success) {
          const payload = (data.result ?? data.result ?? null) as T | null;
          if (payload) return payload;

          addError(new MissingParameterError(response.status, data.errorCodes ?? [], request));
          return null;
        }

        if (status === 200 && !data.success) {
          addError(new MissingParameterError(response.status, data.errorCodes ?? [], request));
          return null;
        }

        addError(new GenericError(response.status, data.errorCodes ?? [], request));
        return null;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<ApiEnvelope<unknown>>;
          if (axiosError) {
            handleCatchError(axiosError);
          }
        }
        addError(new GenericError(-1, [], ''));
        return null;
      } finally {
        setIsSearchingResults(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addError, setIsSearchingResults]
  );

  const post = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(
      url: string,
      body?: U,
      config?: AxiosRequestConfig
    ): Promise<T | null | Blob> => {
      setIsSearchingResults(true);
      clearErrors();
      try {
        const response = await erisAxiosInstance.post<ApiEnvelope<T>>(url, body, config);
        const { status, data, request } = response;
        if (status === 200 && data instanceof Blob) {
          return data;
        } else if (status === 200 && data.success) {
          const payload = (data.result ?? data.result ?? null) as T | null;
          if (payload) return payload;

          addError(new MissingParameterError(status, data.errorCodes ?? [], request));
          return null;
        }

        if (status === 200 && !data.success) {
          addError(new MissingParameterError(status, data.errorCodes ?? [], request));
          return null;
        }

        addError(new GenericError(status, data.errorCodes ?? [], request));
        return null;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<ApiEnvelope<unknown>>;
          if (axiosError) {
            handleCatchError(axiosError);
          }
        }
        addError(new GenericError(-1, [], ''));
        return null;
      } finally {
        setIsSearchingResults(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setIsSearchingResults, addError]
  );

  function handleCatchError(axiosError: AxiosError<ApiEnvelope<unknown>, unknown>) {
    const { config: errorConfig, request: rawRequest } = axiosError;
    const { status } = axiosError.response as AxiosResponse;
    const responseUrl =
      (rawRequest as { responseURL?: string } | undefined)?.responseURL ??
      axios.getUri(errorConfig);
    switch (status) {
      case 401:
        addError(
          new UnauthorizedError(status, resolveErrorMessage(axiosError.response?.data), responseUrl)
        );
        break;
      case 404:
        addError(
          new NotFoundError(status, resolveErrorMessage(axiosError.response?.data), responseUrl)
        );
        break;
      case 500:
        addError(
          new InternalServerError(
            status,
            resolveErrorMessage(axiosError.response?.data),
            responseUrl
          )
        );
        break;
      default:
        addError(new GenericError(-1, [], ''));
        break;
    }
  }

  function resolveErrorMessage(data: ApiEnvelope<unknown> | undefined): string[] {
    if (data === undefined) return ['no data provided'];
    if (data.errorCodes?.length) return data.errorCodes.map((code) => t(`eris.httpError,${code}`));
    return [];
  }

  return { get, post };
}

export default useErisApi;
